import styled from "@emotion/styled"
import { PButtonPure, PSpinner, PText } from "@porsche-design-system/components-react"
import { colorPrimary, spacingStaticSm } from "@porsche-design-system/components-react/emotion"
import type { ActionItemWithLinks } from "@slfinrtl/ui-bff-routes"
import { useIntl } from "react-intl"
import { Link } from "react-router"
import { commonMessages } from "src/common/messages/common.messages"
import { Badge } from "src/components/badge/Badge"
import { Tooltip } from "src/components/tooltip/Tooltip"
import type { VehicleDetailsStage } from "src/screens/vehicleDetails/common/VehicleDetailsStage"

interface RouteProps {
	isExternal?: boolean
	state?: { editStage?: VehicleDetailsStage }
	to: string
}
interface MenuItemProps extends Partial<ActionItemWithLinks> {
	hasNewBadge?: boolean
	isLoading?: boolean
	onClick?: () => void
	route?: RouteProps
}

export const MenuItem = (props: MenuItemProps) => {
	// links is destructured to avoid warning about unknown prop is being sent through to the DOM
	// due do ...rest and ...spread operator which results in rendering an attribute `links="[object Object]`
	// eslint-disable-next-line unused-imports/no-unused-vars
	const { disabledHint, hasNewBadge, isLoading, label, links, onClick, route, ...rest } = props
	const { isExternal, ...routeProps } = route ?? ({} as RouteProps)
	const { formatMessage } = useIntl()

	return disabledHint ? (
		<Tooltip content={disabledHint} appendToBody>
			<DisabledText color="contrast-medium">{label}</DisabledText>
		</Tooltip>
	) : route ? (
		<StyledLink {...routeProps} target={isExternal ? "_blank" : undefined} onClick={onClick} {...rest}>
			{label}
		</StyledLink>
	) : (
		<LinkLoadingWrapper {...rest}>
			<PButtonPure icon="none" onClick={isLoading ? undefined : onClick}>
				{label}
			</PButtonPure>
			{isLoading ? (
				<StyledSpinner size="inherit" />
			) : (
				hasNewBadge && <Badge compact label={formatMessage(commonMessages.new)} color="#4493F6" />
			)}
		</LinkLoadingWrapper>
	)
}

const DisabledText = styled(PText)`
	cursor: not-allowed;
`

const LinkLoadingWrapper = styled.div`
	display: flex;
	align-items: center;
	gap: ${spacingStaticSm};
	
	> *:only-child {
		flex-grow: 1;
	}
`

const StyledLink = styled(Link)`
	color: ${colorPrimary};
	text-decoration: none;
`

const StyledSpinner = styled(PSpinner)`
	height: 25px;
	width: 25px;
`
